<template>
	<div
		v-if="visible && result"
		role="alert"
		class="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950"
		data-testid="ivo-provider-warning"
	>
		<p>{{ result.message }}</p>
		<RouterLink
			v-if="result.action === 'convert-to-ivo'"
			:to="{ name: 'Inkomen' }"
			class="mt-2 inline-block font-semibold underline"
		>
			Ga naar inventarisatie inkomen
		</RouterLink>
		<p v-else-if="result.action === 'requires-nhg'" class="mt-2 font-semibold">
			Deze inkomensverklaring kan alleen bij een aanvraag met NHG worden
			gebruikt.
		</p>
		<p
			v-else-if="result.action === 'choose-other-provider'"
			class="mt-2 font-semibold"
		>
			Kies een andere geldverstrekker voor ondernemersinkomen.
		</p>
	</div>
</template>

<script setup lang="ts">
import { api } from "@/loanLinkApi";
import { ContentType } from "@/generatedApi";
import type { IvoClassification } from "@loanlink-nl/hdn-sdk-js";
import { computed, ref, watch } from "vue";

interface IvoProviderWarningProps {
	receiverCode?: string | null;
	nhg?: boolean;
	productCode?: string;
	hasOnderneming: boolean;
	hasIvo: boolean;
}

type IvoResult = IvoClassification;

const props = defineProps<IvoProviderWarningProps>();

const result = ref<IvoResult>();
let requestSequence = 0;

const hasRelevantIncome = computed(() => props.hasOnderneming || props.hasIvo);

const visible = computed(() => {
	if (!result.value || !hasRelevantIncome.value) return false;

	switch (result.value.status) {
		case "requiresIvo":
		case "requiresIvoForProducts":
			return props.hasOnderneming;
		case "ivoOnlyUnderNhg":
			return props.hasIvo && props.nhg === false;
		case "rejectsOnderneming":
			return true;
		default:
			return false;
	}
});

watch(
	() =>
		[
			props.receiverCode,
			props.nhg,
			props.productCode,
			props.hasOnderneming,
			props.hasIvo,
		] as const,
	async ([receiverCode, nhg, productCode]) => {
		const sequence = ++requestSequence;
		result.value = undefined;
		if (!receiverCode || !hasRelevantIncome.value) return;

		try {
			const response = await api.request<IvoResult>({
				path: "/hdn/ivo-status",
				method: "POST",
				body: {
					messageType: "OfferteAanvraag",
					receiverCode,
					nhg,
					productCode,
				},
				type: ContentType.Json,
				secure: true,
				format: "json",
			});
			if (sequence === requestSequence) result.value = response.data;
		} catch {
			if (sequence === requestSequence) result.value = undefined;
		}
	},
	{ immediate: true },
);
</script>
